<!-- app/javascript/dashboard/routes/dashboard/bulkmessage/components/AddContactsModal.vue -->

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import ContactsPagination from './ContactsPagination.vue';
import CsvMappingModal from './modals/CsvMappingModal.vue';
import FileValidationModal from './modals/FileValidationModal.vue';
import { useAlert } from 'dashboard/composables';
import { debug } from 'shared/helpers/debugHelper';
import {
  formatPhoneWithFlag,
  getCountryFromPhone,
  extractPhoneNumber,
} from 'shared/helpers/phoneHelper';
import { formatFlagDisplayText } from 'shared/helpers/flagEmoji';
import * as XLSX from 'xlsx';

// Import composables
import useCsvImport from '../utils/useCsvImport';
import useFileValidation from '../utils/useFileValidation';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  chatwootContacts: {
    type: Array,
    default: () => [],
  },
  isLoadingContacts: {
    type: Boolean,
    default: false,
  },
  totalContactsCount: {
    type: Number,
    default: 0,
  },
  currentContactsPage: {
    type: Number,
    default: 1,
  },
  totalContactsPages: {
    type: Number,
    default: 1,
  },
  availableLabels: {
    type: Array,
    default: () => [],
  },
  existingRecipients: {
    type: Array,
    default: () => [],
  },
  inboxes: {
    type: Array,
    default: () => [],
  },
  channelTypeFilter: {
    type: String,
    default: '',
  },
  selectedInboxId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['close', 'add-contacts', 'load-contacts-page']);

const store = useStore();

// Refs
const labelDropdownRef = ref(null);
const groupDropdownRef = ref(null);

// State
const showImportModal = ref(false);
const searchQuery = ref('');
const selectedLabels = ref([]);
const selectedGroups = ref([]);
const groupPage = ref(1);
const groupPageSize = ref(25);
const selectedContacts = ref([]);
const selectedContactsCache = ref({});
const searchTimeout = ref(null);
const showLabelDropdown = ref(false);
const showGroupDropdown = ref(false);
const groupSearchQuery = ref('');

const formatContactName = name => formatFlagDisplayText(name || '');

// "Select all" flag — deferred resolution at confirm time
const selectAllMode = ref(false);
const isResolvingSelectAll = ref(false);

// WhatsApp groups state (cached in Vuex store)
const whatsappGroups = computed(() => store.getters['whatsappGroups/getGroups']);
const isLoadingGroups = computed(() => store.getters['whatsappGroups/isLoading']);
const showGroupsButton = computed(() => whatsappGroups.value.length > 0 && !isLoadingGroups.value);

const filteredWhatsappGroups = computed(() => {
  const query = groupSearchQuery.value.trim().toLowerCase();
  if (!query) return whatsappGroups.value;
  return whatsappGroups.value.filter(group => {
    const subject = (group.subject || '').toLowerCase();
    return subject.includes(query);
  });
});

// Computed: all connected evolution inboxes
const evolutionInboxes = computed(() =>
  props.inboxes.filter(
    i => i.integration_type === 'evolution' && i.isConnected
  )
);
const hasEvolutionInboxes = computed(() => evolutionInboxes.value.length > 0);

// Fetch groups from ALL evolution inboxes with 3s backoff
const fetchGroups = () => {
  const evoInboxes = evolutionInboxes.value;
  if (evoInboxes.length === 0) return;
  store.dispatch('whatsappGroups/fetchGroups', evoInboxes);
};

// Import state
const uploadFile = ref(null);
const isProcessingFile = ref(false);
const isDragOver = ref(false);
const uploadError = ref('');
const importResults = ref([]);
const importErrors = ref([]);
const showFieldMappingWizard = ref(false);
const csvWizardStep = ref(0);
const csvHeaders = ref([]);
const rawCsvData = ref([]);
const csvMapping = ref({
  phone: '',
  name: '',
  additional_columns: [],
});
// Store the selected additional columns for passing to parent
const selectedAdditionalColumns = ref([]);

// File validation state
const showFileValidationModal = ref(false);
const fileValidationResult = ref(null);
const currentFileName = ref('');
const estimatedProcessingTime = ref({ seconds: 0, formatted: '' });
const performanceRecommendations = ref([]);
const pendingFile = ref(null); // Store the file being validated

// Store a snapshot of contacts when modal opens
const contactsSnapshot = ref([]);
const snapshotTotalCount = ref(0);
const snapshotTotalPages = ref(1);
const snapshotCurrentPage = ref(1);

// Initialize composables
const { validateFile, estimateProcessingTime, getPerformanceRecommendations } = useFileValidation();

// Initialize CSV import composable
const csvImportComposable = useCsvImport();

// Computed
const contacts = computed(() => {
  let contactsList = [];

  if (props.show && contactsSnapshot.value.length > 0) {
    contactsList = contactsSnapshot.value;
  } else {
    contactsList = props.chatwootContacts || [];
    debug(
      `Received ${contactsList.length} contacts from backend (already filtered), expected page size: ${itemsPerPage.value}`
    );
  }

  // Filter out contacts with "(GROUP)" in the name
  contactsList = contactsList.filter(contact => {
    const name = contact.name || '';
    return !name.includes('(GROUP)');
  });

  return contactsList;
});

// Group mode flag
const isGroupMode = computed(() => selectedGroups.value.length > 0);

// Build full group contacts list (saved + unsaved) from group participants
const displayContacts = computed(() => {
  // No group selected = normal server-side mode
  if (!isGroupMode.value) return contacts.value;

  // Build a map of saved contacts by phone digits for quick lookup
  const allContacts = [
    ...(contactsSnapshot.value || []),
    ...(props.chatwootContacts || []),
  ];
  const savedByPhone = new Map();
  for (const contact of allContacts) {
    const identifier = (contact.identifier || '').replace(/\D/g, '');
    const phone = (contact.phone_number || '').replace(/\D/g, '');
    if (identifier && !savedByPhone.has(identifier)) {
      savedByPhone.set(identifier, contact);
    }
    if (phone && !savedByPhone.has(phone)) {
      savedByPhone.set(phone, contact);
    }
  }

  // Build list from group participants
  const seen = new Set();
  const saved = [];
  const unsaved = [];

  for (const groupId of selectedGroups.value) {
    const group = whatsappGroups.value.find(g => g.id === groupId);
    if (!group?.participants) continue;
    for (const p of group.participants) {
      const digits = p.phoneNumber?.replace(/\D/g, '') || '';
      if (!digits || seen.has(digits)) continue;
      seen.add(digits);

      const existingContact = savedByPhone.get(digits);
      if (existingContact) {
        saved.push(existingContact);
      } else {
        unsaved.push({
          id: `group-${digits}`,
          name: digits,
          phone_number: `+${digits}`,
          email: '',
          labels_with_info: [],
          _isUnsaved: true,
        });
      }
    }
  }

  // Saved first, then unsaved
  return [...saved, ...unsaved];
});

// Client-side filtered list for group mode (search within group contacts)
const filteredDisplayContacts = computed(() => {
  if (!isGroupMode.value) return displayContacts.value;
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return displayContacts.value;
  return displayContacts.value.filter(contact => {
    const name = (contact.name || '').toLowerCase();
    const phone = (contact.phone_number || '').toLowerCase();
    const email = (contact.email || '').toLowerCase();
    return name.includes(query) || phone.includes(query) || email.includes(query);
  });
});

// Client-side paginated slice for group mode
const paginatedDisplayContacts = computed(() => {
  if (!isGroupMode.value) return displayContacts.value;
  const start = (groupPage.value - 1) * groupPageSize.value;
  return filteredDisplayContacts.value.slice(start, start + groupPageSize.value);
});

const isUnsavedContact = contact => !!contact._isUnsaved;

const isLoading = computed(() => {
  if (isGroupMode.value) return false;
  return props.isLoadingContacts;
});
const currentPage = computed(() => {
  if (isGroupMode.value) return groupPage.value;
  if (props.show && snapshotCurrentPage.value > 0) {
    return snapshotCurrentPage.value;
  }
  return props.currentContactsPage;
});
const totalPages = computed(() => {
  if (isGroupMode.value) {
    return Math.ceil(filteredDisplayContacts.value.length / groupPageSize.value) || 1;
  }
  if (props.show && snapshotTotalPages.value > 0) {
    return snapshotTotalPages.value;
  }
  return props.totalContactsPages;
});
const totalContactsCount = computed(() => {
  if (isGroupMode.value) return filteredDisplayContacts.value.length;
  if (props.show && snapshotTotalCount.value > 0) {
    return snapshotTotalCount.value;
  }
  return props.totalContactsCount;
});
const hasMoreToLoad = computed(() => currentPage.value < totalPages.value);

// Computed properties for header checkbox
const isAllVisibleSelected = computed(() => {
  if (paginatedDisplayContacts.value.length === 0) return false;
  return paginatedDisplayContacts.value.every(contact => isContactSelected(contact.id));
});

const isSomeVisibleSelected = computed(() => {
  if (paginatedDisplayContacts.value.length === 0) return false;
  return paginatedDisplayContacts.value.some(contact => isContactSelected(contact.id));
});

// Check if a contact is already in recipients
const isContactAlreadyAdded = phone => {
  const normalizedPhone = phone?.replace(/\D/g, '') || '';
  return props.existingRecipients.some(r => {
    const recipientPhone = r.phone?.replace(/\D/g, '') || '';
    return recipientPhone === normalizedPhone;
  });
};

// Check if contact is selected
const isContactSelected = contactId => {
  return selectAllMode.value || selectedContacts.value.some(c => c.id === contactId);
};

// Effective count: reflects "select all" flag or manual selections
const effectiveSelectedCount = computed(() => {
  if (selectAllMode.value) return totalContactsCount.value;
  return selectedContacts.value.length;
});

// Import Methods
const handleDragEnter = (e) => {
  e.preventDefault();
  e.stopPropagation();
  isDragOver.value = true;
};

const handleDragOver = (e) => {
  e.preventDefault();
  e.stopPropagation();
  isDragOver.value = true;
};

const handleDragLeave = (e) => {
  e.preventDefault();
  e.stopPropagation();
  isDragOver.value = false;
};

const handleDrop = async (e) => {
  e.preventDefault();
  e.stopPropagation();
  isDragOver.value = false;
  
  const files = e.dataTransfer.files;
  if (files.length > 0) {
    const file = files[0];
    await handleFileValidationAndProcessing(file);
  }
};

const handleFileSelect = async e => {
  const file = e.target.files[0];
  if (!file) return;

  await handleFileValidationAndProcessing(file);
};

// New unified file validation and processing function (same as RecipientList.vue)
const handleFileValidationAndProcessing = async (file) => {
  try {
    
    // Store file reference and name
    currentFileName.value = file.name;
    pendingFile.value = file; // Store the file for later use
    
    // Validate file
    const validation = validateFile(file);
    fileValidationResult.value = validation;
    
    
    // Calculate processing time and recommendations
    estimatedProcessingTime.value = estimateProcessingTime(file.size);
    performanceRecommendations.value = getPerformanceRecommendations(file.size / (1024 * 1024));
    
    // File validation completed

    if (!validation.isValid || validation.warnings.length > 0) {
      // Show validation modal for errors or warnings
      showFileValidationModal.value = true;
      return;
    }

    // File is valid and has no warnings, proceed directly
    await processValidatedFile(file);
  } catch (error) {
    console.error('Error during file validation:', error);
    uploadError.value = 'Erro ao validar arquivo. Tente novamente.';
  }
};

// Handle file validation modal actions
const handleFileValidationContinue = async () => {
  
  showFileValidationModal.value = false;
  
  if (fileValidationResult.value?.isValid && pendingFile.value) {
    await processValidatedFile(pendingFile.value);
  } else {
    uploadError.value = 'Erro: arquivo não encontrado ou inválido.';
  }
};

const handleFileValidationCancel = () => {
  
  // Clear pending file
  pendingFile.value = null;
  
  // Clear all data and memory when canceling
  clearFile();
  
  // Reset file input
  const fileInput = document.querySelector('input[type="file"]');
  if (fileInput) {
    fileInput.value = '';
  }
};

// Process file after validation
const processValidatedFile = async (file) => {
  
  uploadFile.value = file;
  uploadError.value = '';
  importResults.value = [];
  importErrors.value = [];

  // Automatically process the file and open the wizard
  await processFile();
  
};

const clearFile = () => {
  
  // Clear local state
  uploadFile.value = null;
  uploadError.value = '';
  importResults.value = [];
  importErrors.value = [];
  isProcessingFile.value = false;
  showFieldMappingWizard.value = false;
  csvWizardStep.value = 0;
  csvHeaders.value = [];
  rawCsvData.value = [];
  csvMapping.value = {
    phone: '',
    name: '',
    additional_columns: [],
  };
  selectedAdditionalColumns.value = [];
  
  // Clear validation state
  showFileValidationModal.value = false;
  fileValidationResult.value = null;
  currentFileName.value = '';
  estimatedProcessingTime.value = { seconds: 0, formatted: '' };
  performanceRecommendations.value = [];
  pendingFile.value = null;
  
  // CRITICAL: Clear composable data to free memory
  if (csvImportComposable) {
    csvImportComposable.parsedCsvRows.value = [];
    csvImportComposable.csvHeaders.value = [];
    csvImportComposable.mapping.phone = '';
    csvImportComposable.mapping.name = '';
    csvImportComposable.mapping.additional_columns = [];
    csvImportComposable.showMapper.value = false;
  }
  
  // Force garbage collection hint
  if (window.gc) {
    window.gc();
  }
  
};

const processFile = async () => {
  if (!uploadFile.value) return;

  // CRITICAL: Clear any previous data before processing new file
  csvHeaders.value = [];
  rawCsvData.value = [];
  
  // Clear composable to prevent memory accumulation
  if (csvImportComposable) {
    csvImportComposable.parsedCsvRows.value = [];
    csvImportComposable.csvHeaders.value = [];
  }

  isProcessingFile.value = true;
  uploadError.value = '';

  try {
    
    // Create a simulated file input event for the composable
    const simulatedEvent = {
      target: {
        files: [uploadFile.value],
        value: null // This will be set by the composable
      }
    };

    // Use the composable's handleFileUpload method
    csvImportComposable.handleFileUpload(simulatedEvent, () => {

      // Success callback - copy data from composable to local state
      csvHeaders.value = csvImportComposable.csvHeaders.value || [];
      rawCsvData.value = csvImportComposable.parsedCsvRows.value || [];
      
      // Copy the mapping from composable if it was auto-detected
      if (csvImportComposable.mapping.phone) {
        csvMapping.value.phone = csvImportComposable.mapping.phone;
      }
      if (csvImportComposable.mapping.name) {
        csvMapping.value.name = csvImportComposable.mapping.name;
      }
      
      // Hide the import modal before showing the field mapping wizard
      showImportModal.value = false;
      // Show the field mapping wizard
      showFieldMappingWizard.value = true;
      isProcessingFile.value = false;
    });
    
  } catch (error) {
    console.error('❌ [AddContactsModal] Error processing file:', error);
    console.error('  Stack:', error.stack);
    uploadError.value =
      'Erro ao processar o arquivo. Verifique o formato e tente novamente.';
    isProcessingFile.value = false;
  }
};

const updateMappingPhone = value => {
  csvMapping.value.phone = value;
};

const updateMappingName = value => {
  csvMapping.value.name = value;
};

const updateMappingAdditional = columns => {
  csvMapping.value.additional_columns = Array.isArray(columns)
    ? [...columns]
    : [];
};

const handleNextStep = () => {
  csvWizardStep.value++;
};

const handlePreviousStep = () => {
  csvWizardStep.value--;
};

const handleSkipStep = () => {
  csvWizardStep.value++;
};

const handleCsvMappingApplied = mappingData => {

  isProcessingFile.value = true;
  uploadError.value = '';

  try {
    // Use the current csvMapping values
    const phoneColumn = csvMapping.value.phone;
    const nameColumn = csvMapping.value.name;
    const additionalColumns = csvMapping.value.additional_columns || [];

    // Apply mapping to raw data
    const contacts = rawCsvData.value.map((row, index) => {

      const contact = { row: index + 2 }; // +2 because index is 0-based and we skip header

      // Apply basic field mapping
      if (phoneColumn && row[phoneColumn]) {
        contact.phone = row[phoneColumn];
      }

      if (nameColumn && row[nameColumn]) {
        contact.name = row[nameColumn];
      }

      // Handle additional columns - store all additional field data
      if (additionalColumns.length > 0) {
        contact.customFields = {};

        additionalColumns.forEach(additionalField => {
          if (
            row[additionalField] !== undefined &&
            row[additionalField] !== null
          ) {
            const value = String(row[additionalField]).trim();

            // Store in customFields for dynamic display
            contact.customFields[additionalField] = value;

            // Also handle email specifically if it looks like an email
            if (
              value &&
              value.includes &&
              value.includes('@') &&
              !contact.email
            ) {
              contact.email = value;
            }
          } else {
            // Store empty value to ensure column appears
            contact.customFields[additionalField] = '';
          }
        });

      }

      return contact;
    });

    // Store the selected additional columns for later use
    selectedAdditionalColumns.value = [...additionalColumns];


    // Validate and process contacts
    const { validContacts, errors } = validateMappedContacts(contacts);


    importResults.value = validContacts;
    importErrors.value = errors;

    if (validContacts.length === 0 && errors.length > 0) {
      uploadError.value = 'Nenhum contato válido encontrado no arquivo.';
    }


    // Close the wizard
    showFieldMappingWizard.value = false;

    // AUTO-CONFIRM IMPORT: If skipContactModal is true, automatically confirm the import
    if (mappingData && mappingData.skipContactModal && validContacts.length > 0) {
      confirmImport();
    } else if (validContacts.length > 0) {
      // Directly confirm import instead of showing modal again
      confirmImport();
    }
  } catch (error) {
    console.error('Error processing mapped fields:', error);
    uploadError.value = 'Erro ao processar os campos mapeados.';
  } finally {
    isProcessingFile.value = false;
  }
};

const validateMappedContacts = contacts => {
  
  const validContacts = [];
  const errors = [];

  contacts.forEach((contact, index) => {
    const contactErrors = [];

    // Validate required fields
    if (!contact.name) {
      contactErrors.push('Nome é obrigatório');
    }

    if (!contact.phone) {
      contactErrors.push('Telefone é obrigatório');
    }

    // Basic validation - just check if phone exists and has reasonable length
    if (contact.phone) {
      const phoneStr = String(contact.phone).trim();
      
      // Very basic validation - just ensure it's not empty after trimming
      if (phoneStr.length === 0) {
        contactErrors.push('Telefone não pode estar vazio');
      } else if (phoneStr.length < 5) {
        contactErrors.push('Telefone muito curto');
      } else if (phoneStr.length > 30) {
        contactErrors.push('Telefone muito longo');
      }
    }

    if (contactErrors.length > 0) {
      errors.push({
        row: contact.row,
        message: contactErrors.join(', '),
      });
    } else {
      // Keep phone exactly as provided by user, just trim whitespace
      const formattedPhone = String(contact.phone).trim();

      const formattedContact = {
        name: String(contact.name || '').trim(),
        phone: formattedPhone,
        email: String(contact.email || '').trim(),
        ddi: '', // No longer extracting DDI, keep it empty
        // Preserve custom fields for dynamic columns
        customFields: contact.customFields || {},
      };


      // Check for duplicates in already imported contacts
      const isDuplicate = validContacts.some(
        vc => vc.phone === formattedContact.phone
      );

      if (!isDuplicate) {
        validContacts.push(formattedContact);
      } else {
      }
    }
  });

  
  return { validContacts, errors };
};

const cancelFieldMapping = () => {
  showFieldMappingWizard.value = false;
  clearFile();
};

const downloadSampleTemplate = () => {
  // Create sample data
  const sampleData = [
    { nome: 'João Silva', telefone: '+5511999887766', email: 'joao@email.com' },
    { nome: 'Maria Santos', telefone: '+5521988776655', email: 'maria@email.com' },
    { nome: 'Carlos Oliveira', telefone: '+5531977665544', email: 'carlos@email.com' },
    { nome: 'Ana Costa', telefone: '+5541966554433', email: 'ana@email.com' },
  ];

  // Create a new workbook
  const workbook = XLSX.utils.book_new();
  
  // Convert data to worksheet
  const worksheet = XLSX.utils.json_to_sheet(sampleData);
  
  // Set column widths for better visibility
  const columnWidths = [
    { wch: 20 }, // nome
    { wch: 20 }, // telefone
    { wch: 25 }, // email
  ];
  worksheet['!cols'] = columnWidths;
  
  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Contatos');
  
  // Generate XLSX file and download
  XLSX.writeFile(workbook, 'modelo-contatos.xlsx');
};

const confirmImport = () => {
  if (!importResults.value || importResults.value.length === 0) {
    useAlert('Nenhum contato para importar');
    return;
  }


  // Format imported contacts for recipient list
  const newRecipients = importResults.value.map((contact, index) => {

    // Keep phone exactly as imported
    const formattedRecipient = {
      id: Date.now() + index + Math.random(),
      name: contact.name,
      ddi: '', // No longer using DDI
      phone: contact.phone, // Keep phone as-is
      email: contact.email || '',
      selectedInbox: props.inboxes.length > 0 ? props.inboxes[0].id : null,
      // Include custom fields for additional columns
      customFields: contact.customFields || {},
    };

    return formattedRecipient;
  });


  // Emit both contacts and selected additional columns for dynamic table display
  emit('add-contacts', newRecipients, selectedAdditionalColumns.value);


  // Clear import data and close
  clearFile();
  closeModal();
};

// Methods
const closeModal = () => {
  
  // Close import modal if open
  showImportModal.value = false;
  
  // Clear all data and memory when closing modal
  clearFile();
  
  emit('close');
};

const debounceSearch = () => {
  clearTimeout(searchTimeout.value);
  searchTimeout.value = setTimeout(() => {
    if (isGroupMode.value) {
      // Client-side filtering: just reset to page 1, computed handles the rest
      groupPage.value = 1;
    } else {
      loadContacts(1);
    }
  }, 500);
};

const clearSearch = () => {
  searchQuery.value = '';
  if (isGroupMode.value) {
    groupPage.value = 1;
  } else {
    loadContacts(1);
  }
};

const selectLabelFilter = (labelTitle) => {
  if (labelTitle === '') {
    // Clear all labels
    selectedLabels.value = [];
  } else {
    // Toggle label selection
    const index = selectedLabels.value.indexOf(labelTitle);
    if (index === -1) {
      selectedLabels.value.push(labelTitle);
    } else {
      selectedLabels.value.splice(index, 1);
    }
  }
  showLabelDropdown.value = false;
  loadContacts(1);
};

const selectGroupFilter = groupId => {
  if (groupId === '') {
    selectedGroups.value = [];
  } else {
    const index = selectedGroups.value.indexOf(groupId);
    if (index === -1) {
      selectedGroups.value.push(groupId);
    } else {
      selectedGroups.value.splice(index, 1);
    }
  }
  showGroupDropdown.value = false;
  groupSearchQuery.value = '';
  clearSelection();
  groupPage.value = 1;
};

const getGroupName = groupId => {
  const group = whatsappGroups.value.find(g => g.id === groupId);
  return group?.subject?.trim() || 'Grupo sem nome';
};

// Items per page state - Default to 25 contacts per page
const itemsPerPage = ref(25);
const itemsPerPageOptions = [10, 25, 50, 100, 200, 500, 1000];

const loadContacts = (page = 1) => {
  debug('Loading contacts with params:', {
    page,
    pageSize: itemsPerPage.value,
    query: searchQuery.value || undefined,
    labels: selectedLabels.value.length > 0 ? selectedLabels.value : undefined,
    labelsFilterMode: 'and',
    channelType: props.channelTypeFilter || undefined,
  });

  // Clear snapshot to allow new filtered data to be loaded
  contactsSnapshot.value = [];

  emit('load-contacts-page', {
    page,
    pageSize: itemsPerPage.value,
    query: searchQuery.value || undefined,
    labels: selectedLabels.value.length > 0 ? selectedLabels.value : undefined,
    labelsFilterMode: 'and', // Use AND logic: show only contacts that have ALL selected labels
    channel_type: props.channelTypeFilter || undefined,
  });
};

const handlePageChange = page => {
  if (isGroupMode.value) {
    groupPage.value = page;
    return;
  }
  // Update snapshot page
  snapshotCurrentPage.value = page;
  loadContacts(page);
};

const handlePageSizeChange = newSize => {
  if (isGroupMode.value) {
    groupPageSize.value = newSize;
    groupPage.value = 1;
    return;
  }
  itemsPerPage.value = newSize;
  // Reset to first page when changing items per page
  loadContacts(1);
};

const toggleContact = contact => {
  // Exit select-all mode when manually toggling individual contacts
  if (selectAllMode.value) {
    selectAllMode.value = false;
  }

  const index = selectedContacts.value.findIndex(c => c.id === contact.id);

  if (index === -1) {
    // Check if already added
    if (isContactAlreadyAdded(contact.phone_number)) {
      useAlert('Este contato já está na lista de destinatários');
      return;
    }

    // Add to selection
    selectedContacts.value.push({
      id: contact.id,
      name: contact.name,
      phone: contact.phone_number,
      email: contact.email,
      identifier: contact.identifier, // Include identifier for Facebook Messenger, etc.
    });

    // Cache the contact
    selectedContactsCache.value[contact.id] = { ...contact };
  } else {
    // Remove from selection
    selectedContacts.value.splice(index, 1);
    delete selectedContactsCache.value[contact.id];
  }
};

const selectAllVisible = () => {
  debug(
    'Selecting all visible contacts. Total visible:',
    paginatedDisplayContacts.value.length
  );
  let selectedCount = 0;
  let skippedCount = 0;

  paginatedDisplayContacts.value.forEach(contact => {
    if (
      !isContactSelected(contact.id) &&
      !isContactAlreadyAdded(contact.phone_number)
    ) {
      selectedContacts.value.push({
        id: contact.id,
        name: contact.name,
        phone: contact.phone_number,
        email: contact.email,
        identifier: contact.identifier,
      });
      selectedContactsCache.value[contact.id] = { ...contact };
      selectedCount++;
    } else {
      skippedCount++;
    }
  });

  debug(
    `Selected ${selectedCount} new contacts, skipped ${skippedCount} (already selected or added)`
  );
};

const selectAll = () => {
  if (isGroupMode.value) {
    // Group mode: all contacts are client-side, select them directly
    filteredDisplayContacts.value.forEach(contact => {
      if (
        !selectedContacts.value.some(c => c.id === contact.id) &&
        !isContactAlreadyAdded(contact.phone_number)
      ) {
        selectedContacts.value.push({
          id: contact.id,
          name: contact.name,
          phone: contact.phone_number,
          email: contact.email,
          identifier: contact.identifier,
        });
        selectedContactsCache.value[contact.id] = { ...contact };
      }
    });
    return;
  }

  // Non-group mode: set flag, resolve at confirm time
  selectAllMode.value = true;
  selectedContacts.value = [];
  selectedContactsCache.value = {};
};

const toggleAllVisible = value => {
  if (selectAllMode.value) selectAllMode.value = false;

  if (value) {
    // Select all visible contacts that aren't already added
    selectAllVisible();
  } else {
    // Deselect all visible contacts
    const visibleContactIds = new Set(paginatedDisplayContacts.value.map(c => c.id));
    selectedContacts.value = selectedContacts.value.filter(
      c => !visibleContactIds.has(c.id)
    );

    // Remove from cache
    paginatedDisplayContacts.value.forEach(contact => {
      delete selectedContactsCache.value[contact.id];
    });
  }
};


const clearSelection = () => {
  selectedContacts.value = [];
  selectedContactsCache.value = {};
  selectAllMode.value = false;
};

const formatRecipientsFromContacts = (contacts) => {
  let defaultInboxId = null;
  if (props.inboxes.length > 0) {
    defaultInboxId = props.inboxes[0].id;
  }

  return contacts.map((contact, index) => ({
    id: Date.now() + index + Math.random(),
    name: contact.name || '',
    ddi: '',
    phone: contact.phone_number || contact.phone || '',
    email: contact.email || '',
    identifier: contact.identifier || '',
    selectedInbox: defaultInboxId,
  }));
};

const resolveSelectAll = async () => {
  isResolvingSelectAll.value = true;
  try {
    const allContacts = [];
    let page = 1;
    let totalPages = 1;

    do {
      const response = await axios.get('/api/v1/bulk_messages/contacts', {
        params: {
          page,
          per_page: 1000,
          query: searchQuery.value || undefined,
          labels: selectedLabels.value.length > 0 ? selectedLabels.value : undefined,
          labels_filter_mode: 'and',
          exclude_empty_phones: true,
          channel_type: props.channelTypeFilter || undefined,
        },
      });

      const contacts = response.data?.data || [];
      const meta = response.data?.meta || {};
      totalPages = Number(meta.total_pages) || 1;

      allContacts.push(...contacts);
      page++;
    } while (page <= totalPages);

    return allContacts;
  } catch {
    useAlert('Erro ao carregar todos os contatos');
    return null;
  } finally {
    isResolvingSelectAll.value = false;
  }
};

const confirmSelection = async () => {
  if (effectiveSelectedCount.value === 0) {
    useAlert('Selecione pelo menos um contato');
    return;
  }

  if (selectAllMode.value) {
    // Resolve: fetch all contacts from API
    const allContacts = await resolveSelectAll();
    if (!allContacts) return;

    const newRecipients = formatRecipientsFromContacts(allContacts);
    emit('add-contacts', newRecipients);
  } else {
    const newRecipients = formatRecipientsFromContacts(selectedContacts.value);
    emit('add-contacts', newRecipients);
  }

  clearSelection();
  closeModal();
};


// Watch for modal open
watch(
  () => props.show,
  newVal => {
    if (newVal) {
      // Reset state when opening
      searchQuery.value = '';
      selectedLabels.value = [];
      showLabelDropdown.value = false;
      clearSelection();

      // Clear import state
      clearFile();

      // Clear snapshot to force initial load
      contactsSnapshot.value = [];
      snapshotTotalCount.value = 0;
      snapshotTotalPages.value = 1;
      snapshotCurrentPage.value = 1;

      loadContacts(1);

      // Fetch WhatsApp groups in parallel (non-blocking)
      fetchGroups();
    } else {
      // Reset group selection when closing (groups stay cached in store)
      selectedGroups.value = [];
      showGroupDropdown.value = false;

      // Clear snapshot when closing
      contactsSnapshot.value = [];
      snapshotTotalCount.value = 0;
      snapshotTotalPages.value = 1;
      snapshotCurrentPage.value = 1;

      // Clear import state
      clearFile();
    }
  }
);

watch(
  () => props.channelTypeFilter,
  (newValue, oldValue) => {
    if (newValue === oldValue) return;
    if (!props.show) return;
    clearSelection();
    loadContacts(1);
  }
);

// Watch for contacts changes to update snapshot
watch(
  () => [
    props.chatwootContacts,
    props.totalContactsCount,
    props.totalContactsPages,
    props.currentContactsPage,
  ],
  ([newContacts, newTotal, newTotalPages, newCurrentPage]) => {
    if (props.show && newContacts && contactsSnapshot.value.length === 0) {
      // Only update snapshot on initial load when modal opens
      // This prevents live updates from affecting the filtered list
      contactsSnapshot.value = [...newContacts];
      snapshotTotalCount.value = newTotal;
      snapshotTotalPages.value = newTotalPages;
      snapshotCurrentPage.value = newCurrentPage;
    }
  }
);

// Handle click outside for label dropdown
const handleClickOutside = event => {
  if (
    labelDropdownRef.value &&
    !labelDropdownRef.value.contains(event.target)
  ) {
    showLabelDropdown.value = false;
  }
  if (
    groupDropdownRef.value &&
    !groupDropdownRef.value.contains(event.target)
  ) {
    showGroupDropdown.value = false;
    groupSearchQuery.value = '';
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <Teleport to="body">
    <transition>
      <div
        v-if="show && !showImportModal && !showFieldMappingWizard"
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-800/20 dark:bg-n-slate-11/15 backdrop-blur-sm"
      >
        <div
          class="bg-white dark:bg-n-slate-1 rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col"
        >
          <!-- Header -->
          <div
            class="px-6 py-4 border-b border-n-slate-3 dark:border-n-slate-4 flex items-center justify-between"
          >
            <div>
              <h2
                class="text-xl font-semibold text-n-slate-12 dark:text-n-slate-12"
              >
                Adicionar Contatos
              </h2>
            </div>
            <button
              class="z-10 ltr:right-2 rtl:left-2 top-2 inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 text-n-slate-12 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent h-10 w-10 p-0 text-sm font-medium justify-center"
              @click="closeModal"
            >
              <span class="i-lucide-x flex-shrink-0"></span>
            </button>
          </div>

          <!-- Content -->
          <div class="flex-1 overflow-hidden flex flex-col min-h-0">
            <!-- Select Contacts -->
            <div
              class="flex-1 overflow-hidden flex flex-col min-h-0"
            >
              <!-- Search and Filters Bar -->
              <div
                class="px-6 pt-4 pb-2"
              >
                <div class="flex gap-3">
                  <!-- Import File Button (moved to left) -->
                  <button
                    class="h-10 px-3 rounded-lg transition-all duration-200 flex items-center gap-2 bg-woot-600 text-white hover:bg-woot-6000 text-sm whitespace-nowrap"
                    @click="showImportModal = true"
                  >
                    <i class="i-lucide-download" />
                    <span>Importar</span>
                  </button>

                  <!-- Search Input -->
                  <div class="flex-1 min-w-[200px]">
                    <div class="relative flex flex-col min-w-0">
                      <span
                        class="i-lucide-search absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 left-2"
                      />
                      <input
                        v-model="searchQuery"
                        type="text"
                        placeholder="Buscar por nome ou telefone..."
                        class="h-10 !pl-7 !py-2 border border-n-slate-3 dark:border-n-slate-6 hover:border-n-slate-6 dark:hover:border-n-slate-7 focus:border-n-brand dark:focus:border-n-brand block w-full reset-base text-sm rounded-lg bg-n-background dark:bg-n-slate-3 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-200 ease-in-out"
                        @input="debounceSearch"
                      />
                      <button
                        v-if="searchQuery"
                        class="absolute right-3 top-1/2 -translate-y-1/2 text-n-slate-9 hover:text-n-slate-11"
                        @click="clearSearch"
                      >
                        <i class="i-lucide-x text-sm" />
                      </button>
                    </div>
                  </div>

                  <!-- Filter by Labels -->
                  <div ref="labelDropdownRef" class="relative">
                      <button
                        class="h-10 w-full px-3 border rounded-lg bg-n-background dark:bg-n-slate-3 text-n-slate-12 dark:text-n-slate-12 hover:bg-n-slate-3 dark:hover:bg-n-slate-4 focus:border-n-brand focus:outline-none flex items-center gap-2 justify-between transition-all duration-200 text-sm"
                        :class="selectedLabels.length > 0 ? 'border-2 border-n-brand' : 'border-n-slate-3 dark:border-n-slate-6'"
                        @click="showLabelDropdown = !showLabelDropdown"
                      >
                        <span class="flex items-center gap-2">
                          <span v-if="selectedLabels.length === 0">
                            Filtrar por etiquetas
                          </span>
                          <span v-else class="flex items-center gap-1">
                            <span
                              v-for="label in selectedLabels.slice(0, 2)"
                              :key="label"
                              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white"
                              :style="{
                                backgroundColor: availableLabels.find(l => (l.title || l.name) === label)?.color || '#999999',
                              }"
                            >
                              {{ label }}
                            </span>
                            <span
                              v-if="selectedLabels.length > 2"
                              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-n-slate-3 text-n-slate-11"
                            >
                              +{{ selectedLabels.length - 2 }}
                            </span>
                          </span>
                        </span>
                        <i class="i-lucide-chevron-down text-n-slate-9" />
                      </button>

                      <!-- Dropdown Menu -->
                      <transition
                        enter-active-class="transition ease-out duration-100"
                        enter-from-class="transform opacity-0 scale-95"
                        enter-to-class="transform opacity-100 scale-100"
                        leave-active-class="transition ease-in duration-75"
                        leave-from-class="transform opacity-100 scale-100"
                        leave-to-class="transform opacity-0 scale-95"
                      >
                        <div
                          v-if="showLabelDropdown"
                          class="absolute z-50 mt-1 w-full bg-white dark:bg-n-slate-1 border border-n-slate-3 dark:border-n-slate-7 rounded-lg shadow-lg max-h-60 overflow-auto min-w-[220px]"
                        >
                          <!-- All contacts option -->
                          <button
                            class="w-full px-4 py-2 text-left hover:bg-n-slate-1 dark:hover:bg-n-slate-3 transition-colors flex items-center justify-between text-n-slate-12 dark:text-n-slate-12"
                            :class="{
                              'bg-n-slate-1 dark:bg-n-slate-3': selectedLabels.length === 0,
                            }"
                            @click="selectLabelFilter('')"
                          >
                            <span>Todos os contatos</span>
                            <i
                              v-if="selectedLabels.length === 0"
                              class="i-lucide-check text-woot-600"
                            />
                          </button>

                          <!-- Label options -->
                          <button
                            v-for="label in availableLabels"
                            :key="label.id"
                            class="w-full px-4 py-2 text-left hover:bg-n-slate-1 dark:hover:bg-n-slate-3 transition-colors flex items-center justify-between text-n-slate-12 dark:text-n-slate-12"
                            :class="{
                              'bg-n-slate-1 dark:bg-n-slate-3':
                                selectedLabels.includes(label.title || label.name),
                            }"
                            @click="
                              selectLabelFilter(label.title || label.name)
                            "
                          >
                            <span class="flex items-center gap-2">
                              <span
                                class="w-3 h-3 rounded-full flex-shrink-0"
                                :style="{
                                  backgroundColor: label.color || '#999999',
                                }"
                              />
                              <span>{{ label.title || label.name }}</span>
                            </span>
                            <i
                              v-if="selectedLabels.includes(label.title || label.name)"
                              class="i-lucide-check text-woot-600"
                            />
                          </button>
                        </div>
                      </transition>
                  </div>

                  <!-- WhatsApp Groups Filter -->
                  <div v-if="hasEvolutionInboxes" ref="groupDropdownRef" class="relative">
                    <button
                      :disabled="isLoadingGroups || !showGroupsButton"
                      class="h-10 w-full px-3 border rounded-lg bg-n-background dark:bg-n-slate-3 text-n-slate-12 dark:text-n-slate-12 hover:enabled:bg-n-slate-3 dark:hover:enabled:bg-n-slate-4 focus:border-n-brand focus:outline-none flex items-center gap-2 justify-between transition-all duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      :class="selectedGroups.length > 0 ? 'border-2 border-n-brand' : 'border-n-slate-3 dark:border-n-slate-6'"
                      @click="showGroupDropdown = !showGroupDropdown"
                    >
                      <span class="flex items-center gap-2 min-w-0 overflow-hidden">
                        <span v-if="isLoadingGroups">
                          Carregando grupos...
                        </span>
                        <span v-else-if="selectedGroups.length === 0">
                          Filtrar por grupos
                        </span>
                        <span v-else class="flex items-center gap-1">
                          <span
                            v-for="groupId in selectedGroups.slice(0, 2)"
                            :key="groupId"
                            class="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-n-slate-3 text-n-slate-12 truncate max-w-[120px]"
                          >
                            {{ getGroupName(groupId) }}
                          </span>
                          <span
                            v-if="selectedGroups.length > 2"
                            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-n-slate-3 text-n-slate-11"
                          >
                            +{{ selectedGroups.length - 2 }}
                          </span>
                        </span>
                      </span>
                      <i class="i-lucide-chevron-down text-n-slate-9" />
                    </button>

                    <!-- Groups Dropdown Menu -->
                    <transition
                      enter-active-class="transition ease-out duration-100"
                      enter-from-class="transform opacity-0 scale-95"
                      enter-to-class="transform opacity-100 scale-100"
                      leave-active-class="transition ease-in duration-75"
                      leave-from-class="transform opacity-100 scale-100"
                      leave-to-class="transform opacity-0 scale-95"
                    >
                      <div
                        v-if="showGroupDropdown"
                        class="absolute z-50 mt-1 w-full bg-white dark:bg-n-slate-1 border border-n-slate-3 dark:border-n-slate-7 rounded-lg shadow-lg max-h-60 overflow-hidden min-w-[220px] flex flex-col"
                      >
                        <!-- Search input -->
                        <div class="p-2 border-b border-n-slate-3 dark:border-n-slate-7">
                          <div class="relative">
                            <span class="i-lucide-search absolute top-1/2 -translate-y-1/2 left-2 text-n-slate-9 size-3.5" />
                            <input
                              v-model="groupSearchQuery"
                              type="text"
                              placeholder="Buscar grupo..."
                              class="w-full h-8 !pl-7 pr-2 text-sm rounded-md border border-n-slate-3 dark:border-n-slate-6 bg-n-background dark:bg-n-slate-3 text-n-slate-12 placeholder:text-n-slate-9 focus:border-n-brand focus:outline-none reset-base"
                              @click.stop
                            />
                          </div>
                        </div>

                        <div class="overflow-auto flex-1">
                          <!-- All contacts option -->
                          <button
                            class="w-full px-4 py-2 text-left hover:bg-n-slate-1 dark:hover:bg-n-slate-3 transition-colors flex items-center justify-between text-n-slate-12 dark:text-n-slate-12"
                            :class="{
                              'bg-n-slate-1 dark:bg-n-slate-3': selectedGroups.length === 0,
                            }"
                            @click="selectGroupFilter('')"
                          >
                            <span>Todos os contatos</span>
                            <i
                              v-if="selectedGroups.length === 0"
                              class="i-lucide-check text-woot-600"
                            />
                          </button>

                          <!-- Group options (filtered) -->
                          <button
                            v-for="group in filteredWhatsappGroups"
                            :key="group.id"
                            class="w-full px-4 py-2 text-left hover:bg-n-slate-1 dark:hover:bg-n-slate-3 transition-colors flex items-center justify-between text-n-slate-12 dark:text-n-slate-12"
                            :class="{
                              'bg-n-slate-1 dark:bg-n-slate-3': selectedGroups.includes(group.id),
                            }"
                            @click="selectGroupFilter(group.id)"
                          >
                            <span class="truncate">{{ group.subject?.trim() || 'Grupo sem nome' }}</span>
                            <i
                              v-if="selectedGroups.includes(group.id)"
                              class="i-lucide-check text-woot-600 flex-shrink-0"
                            />
                          </button>

                          <!-- Empty state -->
                          <div
                            v-if="filteredWhatsappGroups.length === 0"
                            class="px-4 py-3 text-sm text-n-slate-9 text-center"
                          >
                            Nenhum grupo encontrado
                          </div>
                        </div>
                      </div>
                    </transition>
                  </div>

                  <!-- Clear/Select All Button -->
                  <button
                    class="h-10 px-3 rounded-lg transition-all flex items-center gap-2 text-sm whitespace-nowrap border"
                    :class="effectiveSelectedCount > 0 && effectiveSelectedCount >= totalContactsCount ? 'bg-n-brand border-n-brand text-white hover:opacity-90' : 'border-n-slate-3 dark:border-n-slate-6 bg-n-background dark:bg-n-slate-3 text-n-slate-12 dark:text-n-slate-12 hover:bg-n-slate-3 dark:hover:bg-n-slate-4'"
                    @click="effectiveSelectedCount > 0 ? clearSelection() : selectAll()"
                  >
                    <span>{{ effectiveSelectedCount > 0 ? 'Limpar seleção' : 'Selecionar todos' }}</span>
                  </button>
                </div>
              </div>

              <!-- Table Header (outside scroll container) -->
              <div
                v-if="!isLoading && paginatedDisplayContacts.length > 0"
                class="px-6 py-3 bg-white dark:bg-n-slate-1 border-b border-n-slate-3 dark:border-n-slate-4"
              >
                <div class="flex items-center gap-4">
                  <!-- Checkbox column with select all -->
                  <div class="ml-2">
                    <Checkbox
                      :model-value="isAllVisibleSelected"
                      :indeterminate="
                        isSomeVisibleSelected && !isAllVisibleSelected
                      "
                      title="Selecionar todos os visíveis"
                      @update:model-value="toggleAllVisible"
                    />
                  </div>

                  <!-- Headers -->
                  <div
                    class="flex-1 grid grid-cols-12 gap-4 text-sm font-medium text-n-slate-11 dark:text-n-slate-12"
                  >
                    <div class="col-span-4">Nome</div>
                    <div class="col-span-4">Telefone</div>
                    <div class="col-span-4">Etiquetas</div>
                  </div>
                </div>
              </div>

              <!-- Contacts List -->
              <div class="flex-1 overflow-y-auto min-h-0 overscroll-contain">
                <!-- Loading State -->
                <div
                  v-if="isLoading && paginatedDisplayContacts.length === 0"
                  class="flex items-center justify-center h-64"
                >
                  <div class="text-center">
                    <div
                      class="animate-spin w-12 h-12 border-3 border-woot-600 border-t-transparent rounded-full mx-auto mb-4"
                    />
                    <p class="text-n-slate-11">Carregando contatos...</p>
                  </div>
                </div>

                <!-- Empty State -->
                <div
                  v-else-if="!isLoading && paginatedDisplayContacts.length === 0"
                  class="flex items-center justify-center h-64"
                >
                  <div class="text-center">
                    <i class="i-lucide-users text-4xl text-n-slate-9 mb-4" />
                    <p class="text-n-slate-11">
                      {{
                        searchQuery
                          ? 'Nenhum contato encontrado com os filtros aplicados'
                          : 'Nenhum contato disponível'
                      }}
                    </p>
                  </div>
                </div>

                <!-- Contact rows -->
                  <div v-else class="divide-y divide-n-slate-3 dark:divide-n-slate-7 relative dark:divide-none">
                    <div
                      v-for="contact in paginatedDisplayContacts"
                      :key="contact.id"
                      class="px-6 py-3 cursor-pointer transition-all duration-200 hover:bg-n-slate-1 dark:hover:bg-n-slate-2 relative group/row"
                      :class="{
                        'bg-n-slate-2': isContactSelected(contact.id),
                      }"
                      @click="toggleContact(contact)"
                    >
                      <!-- CSS-only tooltip for unsaved contacts (avoids FloatingVue body-appended elements) -->
                      <div
                        v-if="isUnsavedContact(contact)"
                        class="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-n-slate-12 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/row:opacity-100 pointer-events-none transition-opacity z-50"
                      >
                        Esse contato será importado no sistema
                      </div>
                      <div
                        class="flex items-center gap-4"
                        :class="{ 'opacity-50': isUnsavedContact(contact) }"
                      >
                        <!-- Checkbox with more spacing -->
                        <div class="ml-2">
                          <Checkbox
                            :model-value="isContactSelected(contact.id)"
                            @update:model-value="toggleContact(contact)"
                          />
                        </div>

                        <!-- Contact Info -->
                        <div
                          class="flex-1 grid grid-cols-12 gap-4 items-center"
                        >
                          <!-- Name with Channel Badge -->
                          <div class="col-span-4 flex items-center">
                            <div class="flex items-center gap-2 min-w-0">
                              <!-- Channel Badge Icon -->
                              <div
                                v-if="contact.phone_number"
                                v-tooltip.top="'WhatsApp'"
                                class="flex-shrink-0 flex items-center"
                              >
                                <i class="i-ri-whatsapp-fill text-grass-600 dark:text-grass-500 text-base" />
                              </div>
                              <div
                                v-else
                                v-tooltip.top="'Messenger'"
                                class="flex-shrink-0 flex items-center"
                              >
                                <i class="i-ri-messenger-fill text-n-brand text-base" />
                              </div>

                              <!-- Name -->
                              <h4
                                class="font-medium text-n-slate-12 dark:text-n-slate-12 truncate"
                                v-dompurify-html="
                                  formatContactName(contact.name || 'Sem nome')
                                "
                              ></h4>
                            </div>
                          </div>

                          <!-- Phone -->
                          <div class="col-span-4">
                            <p
                              class="text-sm text-n-slate-11 dark:text-n-slate-11"
                            >
                              {{
                                formatPhoneWithFlag(contact.phone_number)
                                  .formatted
                              }}
                            </p>
                          </div>

                          <!-- Labels -->
                          <div class="col-span-4 relative">
                            <div
                              v-if="
                                contact.labels_with_info &&
                                contact.labels_with_info.length > 0
                              "
                              class="flex flex-wrap gap-1"
                            >
                              <span
                                v-for="(
                                  label, idx
                                ) in contact.labels_with_info.slice(0, 2)"
                                :key="`${label.id || idx}-${label.title}`"
                                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white"
                                :style="{
                                  backgroundColor: label.color || '#999999',
                                }"
                                :title="label.title"
                              >
                                {{ label.title }}
                              </span>
                              <div
                                v-if="contact.labels_with_info.length > 2"
                                class="relative inline-block group/labels"
                              >
                                <span
                                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-n-slate-3 text-n-slate-11 cursor-pointer"
                                >
                                  +{{ contact.labels_with_info.length - 2 }}
                                </span>
                                <!-- Tooltip with remaining labels -->
                                <div
                                  class="absolute bottom-full right-0 mb-2 hidden group-hover/labels:block z-50"
                                >
                                  <div class="bg-n-slate-12 dark:bg-n-slate-2 rounded-lg shadow-lg p-2 min-w-max max-w-xs">
                                    <div class="flex flex-wrap gap-1">
                                      <span
                                        v-for="(label, idx) in contact.labels_with_info.slice(2)"
                                        :key="`tooltip-${label.id || idx}-${label.title}`"
                                        class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white"
                                        :style="{
                                          backgroundColor: label.color || '#999999',
                                        }"
                                      >
                                        {{ label.title }}
                                      </span>
                                    </div>
                                    <!-- Arrow -->
                                    <div class="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-n-slate-12 dark:border-t-n-slate-2"></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <span v-else class="text-sm text-n-slate-9">-</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
              </div>
              <!-- closing: scroll container -->

              <!-- Pagination Info -->
              <div
                v-if="paginatedDisplayContacts.length > 0"
                class="px-6 py-3 border-t border-n-slate-3 dark:border-n-slate-4 bg-n-background"
              >
                <ContactsPagination
                  :current-page="currentPage"
                  :total-pages="totalPages"
                  :page-size="isGroupMode ? groupPageSize : itemsPerPage"
                  :page-size-options="itemsPerPageOptions"
                  :show-page-size-selector="true"
                  :loading="isLoading"
                  :current-items-count="paginatedDisplayContacts.length"
                  :total-items-count="totalContactsCount"
                  item-name="contato"
                  @page-change="handlePageChange"
                  @page-size-change="handlePageSizeChange"
                />
              </div>
            </div>
          </div>

          <!-- File Validation Modal -->
          <FileValidationModal
            :show="showFileValidationModal"
            :validation-result="fileValidationResult"
            :file-name="currentFileName"
            :estimated-time="estimatedProcessingTime"
            :recommendations="performanceRecommendations"
            @continue="handleFileValidationContinue"
            @cancel="handleFileValidationCancel"
            @close="handleFileValidationCancel"
          />

          <!-- Footer -->
          <div
            class="px-6 py-4 flex items-center justify-between"
          >
            <div class="text-sm text-n-slate-11 dark:text-n-slate-11">
              <span class="font-medium text-n-slate-12 dark:text-n-slate-12">
                {{ effectiveSelectedCount }} contato{{
                  effectiveSelectedCount !== 1 ? 's' : ''
                }}
                selecionado{{ effectiveSelectedCount !== 1 ? 's' : '' }}
              </span>
            </div>

            <div class="flex gap-3">
              <button
                class="px-4 py-2 bg-n-slate-3 text-n-slate-11 rounded-lg hover:bg-n-slate-4 transition-colors"
                @click="closeModal"
              >
                Cancelar
              </button>
              <button
                :disabled="effectiveSelectedCount === 0 || isResolvingSelectAll"
                class="px-4 py-2 bg-woot-600 text-white rounded-lg hover:bg-woot-6000 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                @click="confirmSelection"
              >
                <span v-if="isResolvingSelectAll">Carregando...</span>
                <span v-else>Adicionar {{ effectiveSelectedCount }} contato{{
                  effectiveSelectedCount !== 1 ? 's' : ''
                }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>

  <!-- Import Modal -->
  <Teleport to="body">
    <transition>
      <div
        v-if="showImportModal"
        class="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-800/20 dark:bg-n-slate-11/15 backdrop-blur-sm"
      >
        <div
          class="bg-white dark:bg-n-slate-1 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
        >
          <!-- Import Modal Header -->
          <div
            class="px-6 py-4 border-b border-n-slate-3 dark:border-n-slate-7 flex items-center justify-between"
          >
            <h2
              class="text-xl font-semibold text-n-slate-12 dark:text-n-slate-12"
            >
              Importar Planilha de Contatos
            </h2>
            <button
              class="z-10 ltr:right-2 rtl:left-2 top-2 inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 text-n-slate-12 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent h-10 w-10 p-0 text-sm font-medium justify-center"
              @click="showImportModal = false; clearFile()"
            >
              <span class="i-lucide-x flex-shrink-0"></span>
            </button>
          </div>

          <!-- Import Modal Content -->
          <div class="flex-1 overflow-y-auto">
            <!-- File Upload Area -->
            <div
              v-if="!importResults || importResults.length === 0"
              class="border-2 border-dashed border-n-slate-3 rounded-lg p-8 m-6 text-center hover:border-woot-600 transition-colors min-h-[400px] flex items-center justify-center"
              :class="{
                'border-woot-600 bg-woot-50 dark:bg-woot-900 ring-2 ring-woot': isDragOver,
                'border-n-ruby-9 bg-n-ruby-1': uploadError
              }"
              @dragenter.prevent="handleDragEnter"
              @dragover.prevent="handleDragOver"
              @dragleave.prevent="handleDragLeave"
              @drop.prevent="handleDrop"
            >
              <!-- Upload State -->
              <div v-if="!uploadFile && !isProcessingFile && !uploadError" class="w-full max-w-md">
                <i class="i-lucide-download text-4xl text-n-slate-9 mb-2" />
                <h3 class="text-xl font-medium text-n-slate-12 mb-6">
                  Arraste ou importe a planilha
                </h3>
                
                <!-- Import Instructions -->
                <div class="mb-6 text-center">
                  <div class="text-sm text-n-slate-11 gap-1 flex flex-col">
                    <span>Os números de telefone serão aceitos exatamente como fornecidos</span>
                    <span>Código do país deve ser incluído no telefone <strong>(+55 ou 55)</strong></span>
                    <span>Colunas obrigatórias: <strong>nome</strong>, <strong>telefone</strong></span>
                  </div>
                </div>
                
                <input
                  ref="fileInput"
                  type="file"
                  accept=".csv,.xlsx"
                  class="hidden"
                  @change="handleFileSelect"
                />
                <div class="flex flex-col gap-2">
                  <button
                    class="w-full px-4 py-2 bg-woot-600 text-white rounded-lg hover:bg-woot-6000 transition-colors"
                    @click="$refs.fileInput.click()"
                  >
                    <i class="i-lucide-download mr-2" />
                    Importar
                  </button>
                  <button
                    class="w-full px-4 py-2 bg-n-slate-3 text-n-slate-11 rounded-lg hover:bg-n-slate-4 transition-colors flex items-center justify-center"
                    @click="downloadSampleTemplate"
                  >
                    <i class="i-lucide-upload mr-2" />
                    Baixar Modelo
                  </button>
                </div>
              </div>

              <!-- Processing State -->
              <div v-if="isProcessingFile" class="text-center">
                <div
                  class="animate-spin w-12 h-12 border-3 border-woot-600 border-t-transparent rounded-full mx-auto mb-4"
                />
                <h3 class="text-lg font-medium text-n-slate-12 mb-2">
                  Processando arquivo...
                </h3>
                <p class="text-sm text-n-slate-11">
                  Analisando estrutura do arquivo
                </p>
              </div>

              <!-- Error State -->
              <div v-if="uploadError" class="text-center">
                <i
                  class="i-lucide-alert-circle text-4xl text-n-ruby-9 mb-4"
                />
                <h3 class="text-lg font-medium text-n-ruby-12 mb-2">
                  Erro no processamento
                </h3>
                <p class="text-sm text-n-ruby-11 mb-4">
                  {{ uploadError }}
                </p>
                <button
                  class="px-4 py-2 bg-n-ruby-9 text-white rounded-lg hover:bg-n-ruby-10 transition-colors"
                  @click="clearFile"
                >
                  Tentar Novamente
                </button>
              </div>
            </div>

            <!-- Import Results -->
            <div
              v-if="importResults && importResults.length > 0"
              class="m-6"
            >
              <div
                class="bg-n-grass-1 border border-n-grass-3 rounded-lg p-4"
              >
                <div class="flex items-center mb-3">
                  <i class="i-lucide-check-circle text-n-grass-9 mr-2" />
                  <h4 class="font-medium text-n-grass-12">
                    {{ importResults.length }} contatos importados com
                    sucesso
                  </h4>
                </div>

                <!-- Preview of imported contacts -->
                <div class="max-h-40 overflow-y-auto">
                  <div class="space-y-2">
                    <div
                      v-for="(contact, index) in importResults.slice(0, 5)"
                      :key="index"
                      class="flex items-center justify-between text-sm bg-white dark:bg-n-slate-2 border border-n-slate-3 dark:border-n-slate-7 rounded p-2"
                    >
                      <div class="flex items-center gap-3">
                        <i class="i-lucide-user text-n-slate-9" />
                        <span
                          class="font-medium text-n-slate-12 dark:text-n-slate-12"
                          v-dompurify-html="formatContactName(contact.name)"
                        ></span
                        >
                        <span
                          class="text-n-slate-11 dark:text-n-slate-11"
                          >{{ contact.phone }}</span
                        >
                        <span
                          v-if="contact.email"
                          class="text-n-slate-9 dark:text-n-slate-10"
                          >{{ contact.email }}</span
                        >
                      </div>
                    </div>
                    <div
                      v-if="importResults.length > 5"
                      class="text-xs text-n-slate-11 text-center py-2"
                    >
                      + {{ importResults.length - 5 }} contatos adicionais
                    </div>
                  </div>
                </div>
              </div>

              <!-- Import Errors -->
              <div
                v-if="importErrors && importErrors.length > 0"
                class="mt-4"
              >
                <div
                  class="bg-n-amber-1 border border-n-amber-3 rounded-lg p-4"
                >
                  <div class="flex items-center mb-3">
                    <i class="i-lucide-alert-triangle text-n-amber-9 mr-2" />
                    <h4 class="font-medium text-n-amber-12">
                      {{ importErrors.length }} erro{{
                        importErrors.length !== 1 ? 's' : ''
                      }}
                      encontrado{{ importErrors.length !== 1 ? 's' : '' }}
                    </h4>
                  </div>

                  <div class="max-h-32 overflow-y-auto space-y-1">
                    <div
                      v-for="(error, index) in importErrors"
                      :key="index"
                      class="text-sm text-n-amber-11 dark:text-n-amber-11 bg-white dark:bg-n-slate-2 border border-n-slate-3 dark:border-n-slate-7 rounded p-2"
                    >
                      Linha {{ error.row }}: {{ error.message }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Import Modal Footer -->
          <div
            class="px-6 py-4 border-t border-n-slate-3 dark:border-n-slate-7 flex items-center justify-between"
          >
            <div class="text-sm text-n-slate-11 dark:text-n-slate-11">
              <span
              v-if="importResults.length > 0"
              class="font-medium text-n-slate-12 dark:text-n-slate-12">
                {{ importResults.length }} contato{{
                  importResults.length !== 1 ? 's' : ''
                }}
                importado{{ importResults.length !== 1 ? 's' : '' }}
              </span>
            </div>

            <div class="flex gap-3">
              <button
                class="px-4 py-2 bg-n-slate-3 text-n-slate-11 rounded-lg hover:bg-n-slate-4 transition-colors"
                @click="showImportModal = false; clearFile()"
              >
                Cancelar
              </button>
              <button
                v-if="importResults && importResults.length > 0"
                class="px-4 py-2 bg-woot-600 text-white rounded-lg hover:bg-woot-6000 transition-colors"
                @click="confirmImport(); showImportModal = false"
              >
                Adicionar {{ importResults.length }} contato{{
                  importResults.length !== 1 ? 's' : ''
                }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>

  <!-- CSV Mapping Modal - Separate from other modals -->
  <CsvMappingModal
    v-if="showFieldMappingWizard"
    :show="showFieldMappingWizard"
    :wizard-step="csvWizardStep"
    :mapping="csvMapping"
    :parsed-csv-rows="rawCsvData"
    :csv-headers="csvHeaders"
    @update:mapping-phone="updateMappingPhone"
    @update:mapping-name="updateMappingName"
    @update:mapping-additional="updateMappingAdditional"
    @next-step="handleNextStep"
    @previous-step="handlePreviousStep"
    @skip-step="handleSkipStep"
    @cancel="cancelFieldMapping"
    @apply-mapping="handleCsvMappingApplied"
  />
</template>
